export type PaginationDisplayPage = number | '...'

export interface GetPaginationDisplayPagesArgs {
  totalPages: number
  currentPage: number
  maxVisibleButtons: number
}

export const getPaginationDisplayPages = ({
  totalPages,
  currentPage,
  maxVisibleButtons,
}: GetPaginationDisplayPagesArgs): PaginationDisplayPage[] => {
  const maxPages = totalPages
  const MAX_VISIBLE_PAGES_LIMIT = maxVisibleButtons

  if (maxPages <= MAX_VISIBLE_PAGES_LIMIT) {
    return Array.from({ length: maxPages }, (_, i) => i + 1)
  }

  const pagesToShow = new Set<number>()
  const middleCount = MAX_VISIBLE_PAGES_LIMIT - 4
  const sideCount = Math.floor(middleCount / 2)

  pagesToShow.add(1)
  pagesToShow.add(maxPages)

  for (let i = currentPage - sideCount; i <= currentPage + sideCount; i++) {
    if (i > 1 && i < maxPages) {
      pagesToShow.add(i)
    }
  }

  if (currentPage <= sideCount + 2) {
    for (let i = 2; i <= MAX_VISIBLE_PAGES_LIMIT - 2; i++) {
      pagesToShow.add(i)
    }
  }

  if (currentPage >= maxPages - sideCount - 1) {
    for (let i = maxPages - (MAX_VISIBLE_PAGES_LIMIT - 3); i < maxPages; i++) {
      pagesToShow.add(i)
    }
  }

  const uniqueSortedPages = Array.from(pagesToShow).sort((a, b) => a - b)

  const estimateVisibleCount = (pages: number[]) => {
    let ellipses = 0
    for (let i = 1; i < pages.length; i++) {
      if (pages[i] > pages[i - 1] + 1) {
        ellipses++
      }
    }
    return pages.length + ellipses
  }

  let visiblePages = uniqueSortedPages
  while (estimateVisibleCount(visiblePages) > MAX_VISIBLE_PAGES_LIMIT) {
    const removable = visiblePages.filter((page) => page !== 1 && page !== maxPages)
    if (removable.length === 0) {
      break
    }

    const pageToRemove = removable.reduce((farthest, page) =>
      Math.abs(page - currentPage) > Math.abs(farthest - currentPage) ? page : farthest,
    )
    visiblePages = visiblePages.filter((page) => page !== pageToRemove)
  }

  const finalPages: PaginationDisplayPage[] = []

  for (let i = 0; i < visiblePages.length; i++) {
    const page = visiblePages[i]
    const previousPage =
      finalPages.length > 0 && typeof finalPages[finalPages.length - 1] === 'number'
        ? (finalPages[finalPages.length - 1] as number)
        : 0

    if (previousPage > 0 && page > previousPage + 1) {
      finalPages.push('...')
    }

    finalPages.push(page)
  }

  return finalPages
}
